import React, { useCallback } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { AssetSymbol, OrderAction, BlockType, BlockConfiguration } from '@tradingblock/types';
import { toAssetSymbol, AssetTypeToSymbolType, OrderBuilderType, isOCCSymbol } from '@tradingblock/api';
import { SymbolName } from '../../components/basic/SymbolName';
import { useDispatcher } from '../../data/global/hooks';
import { useTradeSymbolAction } from '../../components/blocks/hooks/useOrderBlock';
import { useChartSymbolAction, PriceChartBuilderType } from '../../components/blocks/hooks/usePriceChartBlock';
import { TradeOrderButton, ChartButton } from '../../components/blocks/actions/OrderActionButtons';
import { FavoriteRowProps } from './FavoritesContent';

const ClickableSymbol = styled.div`
  cursor: pointer;
  display: inline-block;
  transition: color 0.2s ease;

  &:hover {
    color: var(--teal);
    text-decoration: underline;
  }
`;

interface SymbolCellProps {
  row: { original: FavoriteRowProps };
  isLinked: boolean;
  groupId: string | undefined;
  allBlocks: BlockConfiguration[];
  dispatcher: ReturnType<typeof useDispatcher>['dispatcher'];
}

export const SymbolCell: React.FC<SymbolCellProps> = ({ row, isLinked, groupId, allBlocks, dispatcher }) => {
  const assetSymbol = row.original.AssetSymbol;
  const underlyingSymbol = assetSymbol.underlyingSymbol || assetSymbol.symbol;
  const baseAssetSymbol = toAssetSymbol(
    underlyingSymbol,
    assetSymbol.assetType,
    AssetTypeToSymbolType(assetSymbol.assetType),
    { name: assetSymbol.name, underlyingSymbol, rootSymbol: underlyingSymbol }
  );
  const orderBuilder = useCallback((builder: OrderBuilderType) => {
    return builder.action(OrderAction.Buy);
  }, []);
  const handleTradeClick = useTradeSymbolAction(baseAssetSymbol, orderBuilder, {});

  const handleSymbolClick = () => {
    if (isLinked && groupId) {
      dispatcher.blockGroup.setSymbol(groupId, assetSymbol);
      dispatcher.block.clearHighlightedBlocks();
      dispatcher.block.highlightGroup(groupId);
    } else {
      const unlinkedOrderBlock = _.find(allBlocks, b => {
        if (b.type !== BlockType.Order) return false;
        const blocksInSameGroup = _.filter(allBlocks, bb => bb.groupId === b.groupId);
        return blocksInSameGroup.length === 1;
      });

      if (unlinkedOrderBlock && unlinkedOrderBlock.groupId) {
        dispatcher.blockGroup.setSymbol(unlinkedOrderBlock.groupId, assetSymbol);
        dispatcher.block.highlightBlocks(BlockType.Order);
      } else {
        handleTradeClick(true);
      }
    }
  };

  return (
    <ClickableSymbol onClick={handleSymbolClick}>
      <SymbolName
        symbol={row.original.Symbol}
        underlyingSymbol={assetSymbol.underlyingSymbol || row.original.Symbol}
      />
    </ClickableSymbol>
  );
};

interface TradeCellProps {
  row: { original: FavoriteRowProps };
  groupId: string | undefined;
}

export const TradeCell: React.FC<TradeCellProps> = ({ row, groupId }) => {
  const assetSymbol = row.original.AssetSymbol;
  const underlyingSymbol = assetSymbol.underlyingSymbol || assetSymbol.symbol;
  const baseAssetSymbol = toAssetSymbol(
    underlyingSymbol,
    assetSymbol.assetType,
    AssetTypeToSymbolType(assetSymbol.assetType),
    { name: assetSymbol.name, underlyingSymbol, rootSymbol: underlyingSymbol }
  );
  const orderBuilder = useCallback((builder: OrderBuilderType) => {
    return builder.action(OrderAction.Buy);
  }, []);
  const handleTradeClick = useTradeSymbolAction(baseAssetSymbol, orderBuilder, { groupId });
  return <TradeOrderButton onClick={() => handleTradeClick()} />;
};

interface ChartCellProps {
  row: { original: FavoriteRowProps };
  groupId: string | undefined;
  dispatcher: ReturnType<typeof useDispatcher>['dispatcher'];
}

export const ChartCell: React.FC<ChartCellProps> = ({ row, groupId, dispatcher }) => {
  const assetSymbol = row.original.AssetSymbol;
  const underlyingSymbol = assetSymbol.underlyingSymbol || assetSymbol.symbol;
  const baseAssetSymbol = toAssetSymbol(
    underlyingSymbol,
    assetSymbol.assetType,
    AssetTypeToSymbolType(assetSymbol.assetType),
    { name: assetSymbol.name, underlyingSymbol, rootSymbol: underlyingSymbol }
  );
  const isOCC = isOCCSymbol(assetSymbol.symbol);
  const priceChartBuilder = useCallback((builder: PriceChartBuilderType) => {
    return builder;
  }, []);
  const handleChart = useChartSymbolAction(baseAssetSymbol, priceChartBuilder, { groupId });
  const handleClick = () => {
    handleChart();
    groupId && dispatcher.blockGroup.setSymbol(groupId, assetSymbol);
  };
  if (isOCC) {
    return null;
  }
  return <ChartButton onClick={handleClick} />;
};
